// CookRateIt — the social half of the post-cook flow, one screen.
//
// Merges what used to be three separate phases (diners → rating →
// notes) plus the servings-per-eater stepper into a single scrollable
// form. The 2026-06 post-cook collapse: the kitchen reconcile happens
// on CookKitchenUpdate, everything about the MEAL ITSELF happens here,
// and SAVE commits both in one go.
//
// Pure presentation — all state lives in CookComplete (the
// orchestrator) because save() needs every field at commit time.

import { FONT, COLOR } from "../lib/tokens";

export default function CookRateIt({
  connections = [],          // merged family+friends [{id, name, first, kind, initial, avatarUrl}]
  selectedDiners,            // Set of ids
  toggleDiner,
  ratings,                   // RATINGS constant from CookComplete
  rating, setRating,
  notes, setNotes,
  servingsPerEater, setServingsPerEater,
  nutritionPreview = null,   // recipeNutrition() result or null
  xp = 0,
  saving = false,
  error = null,
  stepLabel = "",
  onBack,
  onSave,
}) {
  const ratingDef = ratings.find(r => r.id === rating);
  const showServings = !!nutritionPreview && nutritionPreview.coverage?.resolved > 0;
  const servingOptions = [0, 0.5, 1, 1.5, 2, 3];
  const servingLabel = (v) => {
    if (v === 0.5) return "½";
    if (v === 1.5) return "1½";
    return String(v);
  };

  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", padding:"40px 24px 32px", overflowY:"auto" }}>
      {stepLabel && (
        <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.gold, letterSpacing:"0.15em", marginBottom:8 }}>
          {stepLabel}
        </div>
      )}
      <h2 style={{ fontFamily:FONT.serif, fontSize:28, fontWeight:300, fontStyle:"italic", color:COLOR.ink, marginBottom:6 }}>
        How'd it go?
      </h2>
      <p style={{ fontFamily:FONT.sans, fontSize:13, color:COLOR.muted, marginBottom:20 }}>
        Rate it, tag who ate with you, jot a note. One screen, then it's in the cookbook.
      </p>

      {/* Rating — 2×2 grid keeps the four options above the fold now
          that diners + notes share the screen. */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(2, 1fr)", gap:8, marginBottom:18 }}>
        {ratings.map(r => {
          const active = rating === r.id;
          return (
            <button
              key={r.id}
              onClick={() => setRating(r.id)}
              className="mcm-focusable"
              style={{
                display:"flex", alignItems:"center", gap:10,
                padding:"13px 14px",
                background: active ? r.bg : COLOR.ground,
                border: `1px solid ${active ? r.color : COLOR.border}`,
                borderRadius:14, cursor:"pointer", transition:"all 0.2s",
                textAlign:"left", touchAction:"manipulation",
              }}
            >
              <span style={{ fontSize:26 }}>{r.emoji}</span>
              <span style={{ flex:1, fontFamily:FONT.serif, fontSize:16, color: active ? r.color : COLOR.ink, fontStyle:"italic" }}>
                {r.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Diners — only when the user has connections. Compact avatar
          grid; tap to toggle. */}
      {connections.length > 0 && (
        <div style={{ marginBottom:18 }}>
          <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.muted, letterSpacing:"0.12em", marginBottom:8 }}>
            WHO ATE WITH YOU? <span style={{ color:COLOR.sub }}>(THEY GET A HEADS-UP)</span>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:8 }}>
            {connections.map(c => {
              const selected = selectedDiners.has(c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => toggleDiner(c.id)}
                  className="mcm-focusable"
                  style={{
                    display:"flex", flexDirection:"column", alignItems:"center", gap:5,
                    padding:"10px 4px",
                    background: selected ? COLOR.goldDeep : COLOR.soil,
                    border: `1px solid ${selected ? COLOR.gold : COLOR.border}`,
                    borderRadius:12, cursor:"pointer", transition:"all 0.2s",
                    touchAction:"manipulation",
                  }}
                >
                  {c.avatarUrl ? (
                    <img
                      src={c.avatarUrl}
                      alt={c.name}
                      referrerPolicy="no-referrer"
                      style={{
                        width:38, height:38, borderRadius:"50%", objectFit:"cover", display:"block",
                        boxShadow: selected ? `0 0 0 2px ${COLOR.gold}` : "none",
                      }}
                    />
                  ) : (
                    <div style={{
                      width:38, height:38, borderRadius:"50%",
                      background: selected ? COLOR.gold : COLOR.edge,
                      color: selected ? COLOR.night : COLOR.dim,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontFamily:FONT.serif, fontSize:17, fontWeight:500,
                    }}>
                      {c.initial}
                    </div>
                  )}
                  <span style={{ fontFamily:FONT.sans, fontSize:12, color: selected ? COLOR.gold : COLOR.dim, textAlign:"center", lineHeight:1.2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"100%" }}>
                    {c.first}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Servings-per-eater — drives cook_logs.servings_per_eater for
          the nutrition tally. Only rendered when the cook resolved any
          macros; no point asking on an untracked recipe. 0 = "saved it
          all" (batch bakes — log bites later via I ATE THIS). */}
      {showServings && (
        <div style={{ marginBottom:18 }}>
          <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.muted, letterSpacing:"0.12em", marginBottom:8 }}>
            SERVINGS EATEN PER PERSON
          </div>
          <div style={{ display:"flex", gap:6 }}>
            {servingOptions.map(v => {
              const active = Math.abs(servingsPerEater - v) < 0.01;
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => setServingsPerEater(v)}
                  className="mcm-focusable"
                  style={{
                    flex:1, padding:"10px 0",
                    background: active ? COLOR.gold : COLOR.ground,
                    border: `1px solid ${active ? COLOR.gold : COLOR.border}`,
                    color: active ? COLOR.night : COLOR.dim,
                    borderRadius:10,
                    fontFamily:FONT.mono, fontSize:13,
                    fontWeight: active ? 700 : 400,
                    cursor:"pointer", letterSpacing:"0.06em",
                    touchAction:"manipulation",
                  }}
                >
                  {servingLabel(v)}
                </button>
              );
            })}
          </div>
          <div style={{ marginTop:6, fontFamily:FONT.mono, fontSize:12, color:COLOR.sub, letterSpacing:"0.06em" }}>
            {servingsPerEater === 0
              ? "SAVED IT ALL — LOG BITES LATER VIA \"I ATE THIS\""
              : `~ ${Math.round((nutritionPreview.perServing.kcal || 0) * servingsPerEater)} kcal PER EATER`}
          </div>
        </div>
      )}

      {/* Notes */}
      <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.muted, letterSpacing:"0.12em", marginBottom:8 }}>
        NOTES <span style={{ color:COLOR.sub }}>(OPTIONAL)</span>
      </div>
      <textarea
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder={rating === "nailed" ? "e.g. The lime rest time made all the difference..." : rating === "rough" ? "e.g. Pan wasn't hot enough. Next time crank it earlier." : "Anything you'd remember for next time..."}
        rows={3}
        style={{ width:"100%", padding:"12px 14px", background:COLOR.ground, border:`1px solid ${COLOR.border}`, borderRadius:12, fontFamily:FONT.sans, fontSize:14, color:COLOR.ink, outline:"none", resize:"none", boxSizing:"border-box", marginBottom:14 }}
      />

      {/* Summary strip — visual confirm of what's being saved. */}
      <div style={{ padding:"12px 14px", background:COLOR.deep, border:`1px solid ${ratingDef?.border || COLOR.edge}`, borderRadius:12, display:"flex", alignItems:"center", gap:12, marginBottom:8 }}>
        {ratingDef && <span style={{ fontSize:24 }}>{ratingDef.emoji}</span>}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontFamily:FONT.sans, fontSize:13, color: ratingDef?.color || COLOR.dim }}>
            {ratingDef?.label || "Pick a rating to save"}
          </div>
          <div style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.muted, marginTop:2 }}>
            {selectedDiners.size > 0
              ? `with ${selectedDiners.size} ${selectedDiners.size === 1 ? "person" : "people"}`
              : "ate alone"}
            {" · "}
            +{xp} XP
          </div>
        </div>
        {(rating === "good" || rating === "nailed") && (
          <span style={{ fontFamily:FONT.mono, fontSize:12, color:COLOR.gold, background:COLOR.goldDeep, border:`1px solid ${COLOR.goldDim}`, borderRadius:4, padding:"2px 6px", letterSpacing:"0.1em" }}>
            ★ FAVORITE
          </span>
        )}
      </div>

      {error && (
        <div style={{ marginBottom:12, padding:"10px 12px", background:COLOR.roseDeep, border:`1px solid ${COLOR.roseDim}`, borderRadius:10, fontFamily:FONT.sans, fontSize:12, color:COLOR.rose }}>
          {error}
        </div>
      )}

      <div style={{ display:"flex", gap:10, marginTop:"auto" }}>
        <button onClick={onBack} disabled={saving}
          className="mcm-focusable"
          style={{ flex:1, padding:"14px", background:COLOR.soil, border:`1px solid ${COLOR.border}`, borderRadius:12, fontFamily:FONT.mono, fontSize:12, color:COLOR.dim, cursor: saving ? "not-allowed" : "pointer", letterSpacing:"0.08em", touchAction:"manipulation" }}>
          ← BACK
        </button>
        <button
          onClick={onSave}
          disabled={saving || !rating}
          className={saving || !rating ? "" : "mise-cta mcm-focusable"}
          style={{ flex:2, padding:"14px", background: (saving || !rating) ? COLOR.soil : COLOR.gold, border:"none", borderRadius:12, fontFamily:FONT.mono, fontSize:12, fontWeight:600, color: (saving || !rating) ? COLOR.sub : COLOR.night, cursor: (saving || !rating) ? "not-allowed" : "pointer", letterSpacing:"0.08em", touchAction:"manipulation" }}>
          {saving ? "SAVING..." : "SAVE TO COOKBOOK →"}
        </button>
      </div>
    </div>
  );
}
